import { useCallback, useRef } from 'react';

import { Tooltip, type TooltipRefProps } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { useGetAllTags } from '@/hooks/queries/useTags';

const TOOLTIP_CLASS = 'tag-definition-tooltip';
const DEFAULT_BORDER_COLOR = 'var(--org-color)';

const TaggedWordTooltip = () => {
	const { data: tagInfo, isLoading } = useGetAllTags();
	const reactTooltipRef = useRef<TooltipRefProps | null>(null);

	const applyTooltipStyles = useCallback(() => {
		// Run after paint so the tooltip wrapper is in the DOM
		requestAnimationFrame(() => {
			const anchor = reactTooltipRef.current?.activeAnchor;
			if (!anchor || !tagInfo?.length) return;

			const tagId = anchor.getAttribute('data-tip');
			if (!tagId) return;

			const matchingTag = tagInfo.find((tag) => tag.tag_id === tagId);
			const color = matchingTag?.tag_color
				? matchingTag.tag_color.replace(')', ', 0.4)').replace('rgb', 'rgba')
				: DEFAULT_BORDER_COLOR;
			const borderColor = color || DEFAULT_BORDER_COLOR;

			const tooltip = document.querySelector(`.${TOOLTIP_CLASS}`) as HTMLElement;
			if (tooltip) {
				tooltip.style.borderColor = borderColor;
				tooltip.style.borderWidth = '3px';
				tooltip.style.width = 'fit-content';
				tooltip.style.backgroundColor = 'var(--c-white)';
				tooltip.style.maxWidth = '92vw';
				// Let the inner content div own the scroll — outer just clips the border box
				tooltip.style.maxHeight = 'none';
				tooltip.style.overflow = 'visible';
				tooltip.style.zIndex = '9999';
			}
			document.documentElement.style.setProperty('--tooltip-arrow-color', borderColor);
		});
	}, [tagInfo]);

	const renderTooltipContent = ({ activeAnchor }: { activeAnchor: HTMLElement | null }) => {
		if (!activeAnchor) return null;

		const alignment = document?.querySelector('.course-alignment');
		if (alignment?.classList.contains('hidden-tags')) {
			return null;
		}

		const tagId = activeAnchor.getAttribute('data-tip');
		if (!tagId) return null;

		const matchingTag = tagInfo?.find((tag) => tag.tag_id === tagId);
		if (!matchingTag?.tag_definition) return null;

		return (
			<div
				className="flex flex-col text-wrap whitespace-pre-wrap"
				style={{
					color: 'var(--body-text)',
					fontFamily: 'var(--font-bold)',
					// Scrollable content box — keeps everything within the viewport
					maxHeight: '55vh',
					overflowY: 'scroll',
					// iOS Safari requires these for touch-based scroll to work
					WebkitOverflowScrolling: 'touch',
					touchAction: 'pan-y',
					paddingRight: '4px',
				}}
			>
				<strong className="mx-auto text-[1.6rem]">{matchingTag?.tag_name}</strong>
				<span className="text-[1.5rem]" style={{ fontFamily: 'var(--font-regular)' }}>
					{matchingTag?.tag_definition}
				</span>
			</div>
		);
	};

	if (isLoading) {
		return <div />;
	}

	return (
		<Tooltip
			id="tag-tooltip"
			anchorSelect=".tagged-word"
			render={renderTooltipContent}
			afterShow={applyTooltipStyles}
			data-testid="tooltip"
			className={TOOLTIP_CLASS}
			clickable
			opacity={0.95}
			style={{
				backgroundColor: 'var(--c-white)',
				borderRadius: '6px',
				borderColor: DEFAULT_BORDER_COLOR,
				borderWidth: '3px',
				width: 'fit-content',
				maxWidth: '92vw',
				zIndex: 9999,
			}}
			ref={reactTooltipRef}
		/>
	);
};

export default TaggedWordTooltip;
